import { useNavigate, useLocation } from "react-router-dom";
import { Heart, Search, MapPin, UserStar } from "lucide-react";
import { useContext } from "react";
import { MasajidContext } from "../contexts/MasajidContext";

function BottomNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getMyMasjid, language, changeLanguage } = useContext(MasajidContext);

  const myMasjidUsername = getMyMasjid();
  const isMyMasjidPage =
    myMasjidUsername && location.pathname === `/masjid/${myMasjidUsername}`;
  const isHomePage = location.pathname === "/";
  const isFavouritesPage = location.pathname === "/favourites";
  const isNearbyPage = location.pathname === "/nearby";

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(4, 27, 35, 0.95)",
        backdropFilter: "blur(10px)",
        borderTop: "2px solid rgba(27, 179, 200, 0.3)",
        padding: "12px 12px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "12px",
        zIndex: 1000,
        boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.3)",
      }}
    >
      {/* Home - Always visible */}
      <button
        onClick={() => navigate("/")}
        style={{
          backgroundColor: isHomePage
            ? "rgba(255, 255, 255, 0.2)"
            : "transparent",
          border: isHomePage
            ? "2px solid rgba(255, 255, 255, 0.7)"
            : "2px solid rgba(255, 255, 255, 0.5)",
          borderRadius: "50%",
          padding: "12px",
          color: isHomePage
            ? "rgba(255, 255, 255, 0.7)"
            : "rgba(255, 255, 255, 0.5)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          width: "48px",
          height: "48px",
        }}
        onTouchStart={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
        }
        onTouchEnd={(e) =>
          (e.currentTarget.style.backgroundColor = isHomePage
            ? "rgba(255, 255, 255, 0.2)"
            : "transparent")
        }
        onMouseOver={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.backgroundColor = isHomePage
            ? "rgba(255, 255, 255, 0.2)"
            : "transparent")
        }
        aria-label="Home"
      >
        <Search size={22} strokeWidth={2.5} />
      </button>

      {/* My Masjid - Show only if user has one, always visible when exists */}
      {myMasjidUsername && (
        <button
          onClick={() => navigate(`/masjid/${myMasjidUsername}`)}
          style={{
            backgroundColor: isMyMasjidPage
              ? "rgba(34, 197, 94, 0.2)"
              : "transparent",
            border: isMyMasjidPage
              ? "2px solid #22c55e"
              : "2px solid rgba(34, 197, 94, 0.7)",
            borderRadius: "50%",
            padding: "12px",
            color: "#22c55e",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
            width: "48px",
            height: "48px",
          }}
          onTouchStart={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(34, 197, 94, 0.4)")
          }
          onTouchEnd={(e) =>
            (e.currentTarget.style.backgroundColor = isMyMasjidPage
              ? "rgba(34, 197, 94, 0.2)"
              : "transparent")
          }
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(34, 197, 94, 0.2)")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = isMyMasjidPage
              ? "rgba(34, 197, 94, 0.2)"
              : "transparent")
          }
          aria-label="My Masjid"
        >
          <UserStar size={20} strokeWidth={2.5} />
        </button>
      )}

      {/* Favourites - Always visible */}
      <button
        onClick={() => navigate("/favourites")}
        style={{
          backgroundColor: isFavouritesPage
            ? "rgba(230, 57, 70, 0.2)"
            : "transparent",
          border: isFavouritesPage
            ? "2px solid #e63946"
            : "2px solid rgba(230, 57, 70, 0.7)",
          borderRadius: "50%",
          padding: "12px",
          color: "#e63946",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          width: "48px",
          height: "48px",
        }}
        onTouchStart={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(230, 57, 70, 0.4)")
        }
        onTouchEnd={(e) =>
          (e.currentTarget.style.backgroundColor = isFavouritesPage
            ? "rgba(230, 57, 70, 0.2)"
            : "transparent")
        }
        onMouseOver={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(230, 57, 70, 0.2)")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.backgroundColor = isFavouritesPage
            ? "rgba(230, 57, 70, 0.2)"
            : "transparent")
        }
        aria-label="Favourites"
      >
        <Heart size={20} strokeWidth={2.5} />
      </button>

      {/* Nearby - Always visible */}
      <button
        onClick={() => navigate("/nearby")}
        style={{
          backgroundColor: isNearbyPage
            ? "rgba(27, 179, 200, 0.2)"
            : "transparent",
          border: isNearbyPage
            ? "2px solid rgb(27, 179, 200)"
            : "2px solid rgba(27, 179, 200, 0.7)",
          borderRadius: "50%",
          padding: "12px",
          color: "rgb(27, 179, 200)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          width: "48px",
          height: "48px",
        }}
        onTouchStart={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(27, 179, 200, 0.4)")
        }
        onTouchEnd={(e) =>
          (e.currentTarget.style.backgroundColor = isNearbyPage
            ? "rgba(27, 179, 200, 0.2)"
            : "transparent")
        }
        onMouseOver={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(27, 179, 200, 0.2)")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.backgroundColor = isNearbyPage
            ? "rgba(27, 179, 200, 0.2)"
            : "transparent")
        }
        aria-label="Nearby Masajid"
      >
        <MapPin size={22} strokeWidth={2.5} />
      </button>

      {/* Language Toggle */}
      <button
        onClick={() =>
          changeLanguage(language === "english" ? "urdu" : "english")
        }
        style={{
          backgroundColor: "transparent",
          border: "2px solid rgba(255, 255, 255, 0.5)",
          borderRadius: "50%",
          padding: "12px",
          color: "rgba(255, 255, 255, 0.8)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          width: "48px",
          height: "48px",
          fontSize: language === "english" ? "14px" : "12px",
          fontWeight: "bold",
        }}
        aria-label="Toggle Language"
        lang={language !== "urdu" && "ur"}
      >
        {language === "english" ? "اردو" : "EN"}
      </button>
    </div>
  );
}

export default BottomNavBar;
